// PaymentDialog.js - Enhanced Version with Conditional Payment Methods Display and Debt Payment Support
import React, { useState, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Alert,
  Chip,
  CircularProgress,
  LinearProgress
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  CheckCircle as CheckCircleIcon,
  Security as SecurityIcon,
  Payment as PaymentIcon,
  Phone as PhoneIcon,
  Payments as PaymentsIcon,
  AccessTime as AccessTimeIcon,
  AccountBalance as DebtIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';
import { formatCurrency, successMessage } from '../Message';
import { initiatepayment, checkpaymentstatus, PaymentProcess } from '../BaseURL';

const PaymentDialog = ({
  open,
  onClose,
  selectedTotal,
  selectedPatient,
  selectedCharges,
  onPaymentSuccess,
  currentUser,
  serviceCharges,
  investigationCharges,
  medicationCharges,
  otherCharges,
  // New props for conditional display
  showPaymentMethods = true, // Show/hide payment methods section
  allowedPaymentMethods = ['cash', 'mobile_money', 'credit'], // Array of allowed methods
  defaultPaymentMethod = 'cash', // Default selected method
  enableMobileMoneyValidation = true, // Enable/disable mobile money validation
  showSecurityNotice = true, // Show/hide security notice
  // New parameter for debt payment
  isDebtPayment = false, // Flag to indicate if this is a debt payment
  debtDetails = null, // Object containing debt details like debtId, originalAmount, remainingAmount, etc.
  paymentType = 'normal' // 'normal', 'debt', 'partial_debt'
}) => {
  const [paymentMethod, setPaymentMethod] = useState(defaultPaymentMethod);
  const [mobileMoneyData, setMobileMoneyData] = useState({
    payeeMobileNumber: '',
    channel: ''
  });
  const [mobileMoneyLoading, setMobileMoneyLoading] = useState(false);
  const [mobileMoneyError, setMobileMoneyError] = useState('');
  const [mobileMoneyDigits, setMobileMoneyDigits] = useState(Array(10).fill(''));
  const [statusCheckProgress, setStatusCheckProgress] = useState(0);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [paymentStatusMessage, setPaymentStatusMessage] = useState('');
  
  // Refs for cleanup
  const statusCheckIntervalRef = useRef(null);
  const timeoutRef = useRef(null);

  const mobileMoneyChannels = [
    { id: 'mtn-gh', label: 'MTN', icon: '📱' },
    { id: 'vodafone-gh', label: 'Telecel', icon: '📱' },
    { id: 'tigo-gh', label: 'AirtelTigo', icon: '📱' }
  ];

  
  const allPaymentMethods = [
    { 
      id: 'cash', 
      label: 'Cash Payment', 
      icon: PaymentsIcon,
      description: isDebtPayment ? 'Pay debt with physical cash' : 'Pay with physical cash'
    },
    { 
      id: 'mobile_money', 
      label: 'Mobile Money', 
      icon: PhoneIcon,
      description: isDebtPayment ? 'Pay debt via MTN, Vodafone, AirtelTigo' : 'Pay via MTN, Vodafone, AirtelTigo'
    },

     { 
      id: 'mobile_money(manual)', 
      label: 'Mobile Money(manual)', 
      icon: PhoneIcon,
      description: isDebtPayment ? 'Pay debt via MTN, Vodafone, AirtelTigo (mobile_money(manual))' : 'Pay via MTN, Vodafone, AirtelTigo'
    },

    { 
      id: 'credit', 
      label: 'Credit Bill', 
      icon: CreditCardIcon,
      description:  'Credit Patient Bill'
    }
  ];

  // Filter payment methods based on allowedPaymentMethods prop
  const paymentMethods = allPaymentMethods.filter(method => 
    allowedPaymentMethods.includes(method.id)
  );

  // Helper function to get selected charges (same as handleRegularPayment)
  const getSelectedCharges = () => {
    const selectedServiceCharges = serviceCharges.filter(charge => 
      selectedCharges.includes(charge.id) && charge.status !== 'paid' && charge.status !== 'credit'
    );
    
    const selectedInvestigationCharges = investigationCharges.filter(charge => 
      selectedCharges.includes(charge.id) && charge.status !== 'paid' && charge.status !== 'credit'
    );
    
    const selectedMedicationCharges = medicationCharges.filter(charge => 
      selectedCharges.includes(charge.id) && charge.status !== 'paid' && charge.status !== 'credit'
    );
    
    const selectedOtherCharges = otherCharges.filter(charge => 
      selectedCharges.includes(charge.id) && charge.status !== 'paid' && charge.status !== 'credit'
    );

    return {
      selectedServiceCharges,
      selectedInvestigationCharges,
      selectedMedicationCharges,
      selectedOtherCharges
    };
  };

  // Helper function to format phone number to international format
  const formatToInternational = (number) => {
    const cleanNumber = number.replace(/\D/g, '');
    
    if (cleanNumber.startsWith('233')) {
      return cleanNumber;
    }
    
    if (cleanNumber.startsWith('0')) {
      return '233' + cleanNumber.substring(1);
    }
    
    return '233' + cleanNumber;
  };

  // Enhanced status checking with visual feedback
  const performStatusCheck = async (clientReference, maxAttempts = 12, attemptInterval = 10000) => {
    let attempts = 0;
    setIsCheckingStatus(true);
    setStatusCheckProgress(0);
    
    return new Promise((resolve) => {
      const checkStatus = async () => {
        attempts++;
        const progress = (attempts / maxAttempts) * 100;
        setStatusCheckProgress(progress);
        
        try {
          setPaymentStatusMessage(`Checking payment status... (${attempts}/${maxAttempts})`);
          
          const statusResult = await checkPaymentStatus(clientReference);
          
          if (statusResult.success && statusResult.status === 'success') {
            // Payment successful
            clearInterval(statusCheckIntervalRef.current);
            setIsCheckingStatus(false);
            setStatusCheckProgress(100);
            setPaymentStatusMessage('Payment verified successfully!');
            
            const paymentTypeText = isDebtPayment ? 'Debt Payment' : 'Payment';
            await Swal.fire({
              title: `${paymentTypeText} Successful! 🎉`,
              text: `${paymentTypeText} of ${formatCurrency(selectedTotal)} has been confirmed.`,
              icon: 'success',
              confirmButtonText: 'Great!',
              timer: 5000
            });
            
            if (onPaymentSuccess) {
              onPaymentSuccess();
            }
            
            resolve({ success: true, status: 'success' });
            return;
          }
          
          if (statusResult.success && statusResult.status === 'failed') {
            // Payment failed
            clearInterval(statusCheckIntervalRef.current);
            setIsCheckingStatus(false);
            
            await Swal.fire({
              title: 'Payment Failed',
              text: `The mobile money ${isDebtPayment ? 'debt ' : ''}payment was not completed. Please try again.`,
              icon: 'error',
              confirmButtonText: 'OK'
            });
            
            resolve({ success: false, status: 'failed' });
            return;
          }
          
          // Still pending, continue checking if we haven't exceeded max attempts
          if (attempts >= maxAttempts) {
            clearInterval(statusCheckIntervalRef.current);
            setIsCheckingStatus(false);
            
            await Swal.fire({
              title: 'Payment Status Unknown',
              html: `
                <p>We couldn't confirm your ${isDebtPayment ? 'debt ' : ''}payment status automatically.</p>
                <p><strong>What to do:</strong></p>
                <ul style="text-align: left; margin: 10px 0;">
                  <li>Check your mobile money balance</li>
                  <li>If money was deducted, contact support with reference: <strong>${clientReference}</strong></li>
                  <li>If no money was deducted, you can try again</li>
                </ul>
              `,
              icon: 'warning',
              confirmButtonText: 'I Understand',
              allowOutsideClick: false
            });
            
            resolve({ success: false, status: 'timeout' });
          }
        } catch (error) {
          console.error('Status check error:', error);
          // Continue checking unless max attempts reached
          if (attempts >= maxAttempts) {
            clearInterval(statusCheckIntervalRef.current);
            setIsCheckingStatus(false);
            resolve({ success: false, status: 'error', error: error.message });
          }
        }
      };
      
      // Start status checking immediately
      checkStatus();
      
      // Set up interval for subsequent checks
      statusCheckIntervalRef.current = setInterval(checkStatus, attemptInterval);
    });
  };

  const handleMobileMoneyChannelChange = useCallback((e) => {
    setMobileMoneyData(prev => ({ 
      ...prev, 
      channel: e.target.value 
    }));
  }, []);

  const handleMobileMoneyPayment = async () => {
    const joinedNumber = mobileMoneyDigits.join('');

    // Input validation (only if validation is enabled)
    if (enableMobileMoneyValidation) {
      if (joinedNumber.length !== 10) {
        setMobileMoneyError('Please enter a valid 10-digit number');
        return;
      }

      if (!mobileMoneyData.channel) {
        setMobileMoneyError('Please select a mobile money provider');
        return;
      }
    }

    // Validate amount
    if (!selectedTotal || selectedTotal <= 0) {
      setMobileMoneyError('Invalid payment amount');
      return;
    }

    // Validate patient data
    if (!selectedPatient || !selectedPatient.id) {
      setMobileMoneyError('Patient information is missing');
      return;
    }

    setMobileMoneyLoading(true);
    setMobileMoneyError('');
    
    try {
      // Get selected charges (same structure as handleRegularPayment)
      const {
        selectedServiceCharges,
        selectedInvestigationCharges,
        selectedMedicationCharges,
        selectedOtherCharges
      } = getSelectedCharges();

      // Prepare payment description based on payment type
      let paymentDescription = `Hospital Bill - Patient: ${selectedPatient.fullName || selectedPatient.name}`;
      if (isDebtPayment) {
        paymentDescription = `Debt Payment - Patient: ${selectedPatient.fullName || selectedPatient.name}`;
        if (debtDetails?.debtId) {
          paymentDescription += ` (Debt ID: ${debtDetails.debtId})`;
        }
      }

      // Prepare payment payload with selected charges and debt information
      const paymentPayload = {
        totalAmount: parseFloat(selectedTotal),
        description: paymentDescription,
        patientId: selectedPatient.id,
        patientName: (selectedPatient.fullName || selectedPatient.name || '').trim(),
        patientEmail: selectedPatient.email || "patient@hospital.com",
        patientPhone: formatToInternational(joinedNumber),
        billItems: isDebtPayment ? "Debt Payment" : "Hospital Services",
        // Add selected charges data for backend processing
        selectedCharges: {
          selectedServiceCharges,
          selectedInvestigationCharges,
          selectedMedicationCharges,
          selectedOtherCharges
        },
        // Additional patient info
        patient_name: selectedPatient.fullName,
        tel: selectedPatient.tel,
        res: selectedPatient.res,
        // Payment method and user info
        paymentMethod: 'mobile_money',
        currentUser,
        // New debt payment fields
        isDebtPayment,
        paymentType,
        debtDetails: isDebtPayment ? {
          debtId: debtDetails?.debtId || null,
          originalAmount: debtDetails?.originalAmount || selectedTotal,
          remainingAmount: debtDetails?.remainingAmount || selectedTotal,
          debtDate: debtDetails?.debtDate || null,
          debtDescription: debtDetails?.debtDescription || null,
          ...debtDetails
        } : null
      };
      
      console.log('Sending mobile money payment request:', paymentPayload);

      // Make the API call to initiate payment
      const response = await axios.post(initiatepayment, paymentPayload, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        timeout: 30000
      });

      console.log('Payment API Response:', response.data);

      // Handle successful response
      if (response.data.success) {
        const clientReference = response.data.data?.clientReference;
        
        if (!clientReference) {
          throw new Error('No client reference received from payment gateway');
        }

        // Show initial success message with debt payment context
        const paymentTypeText = isDebtPayment ? 'debt payment' : 'payment';
        await Swal.fire({
          title: `${isDebtPayment ? 'Debt ' : ''}Payment Request Sent! 📱`,
          html: `
            <div style="text-align: center;">
              <p>A mobile money ${paymentTypeText} prompt has been sent to <strong>${joinedNumber}</strong></p>
              <p style="color: #e67e22; font-weight: bold;">Please check your phone and complete the ${paymentTypeText}</p>
              <div style="background: #fff3cd; padding: 15px; border-radius: 8px; margin: 15px 0;">
                <p style="margin: 0; font-size: 14px;">
                  <strong>Steps to complete:</strong><br>
                  1. Check your phone for the payment prompt<br>
                  2. Enter your mobile money PIN<br>
                  3. Confirm the ${paymentTypeText}<br>
                  4. Wait for verification
                </p>
              </div>
              ${isDebtPayment && debtDetails?.debtId ? 
                `<p style="font-size: 12px; color: #666;">Debt ID: <strong>${debtDetails.debtId}</strong></p>` : ''
              }
              <p style="font-size: 12px; color: #666;">
                Reference: <strong>${clientReference}</strong>
              </p>
            </div>
          `,
          icon: 'info',
          confirmButtonText: `I've Completed ${isDebtPayment ? 'Debt ' : ''}Payment`,
          timer: 15000,
          allowOutsideClick: false
        });
        
        // Start automated status checking
        setMobileMoneyLoading(false);
        const statusResult = await performStatusCheck(clientReference);
        
        if (statusResult.success && statusResult.status === 'success') {
          onClose();
        }
        
      } else {
        // Handle API error response
        const errorMessage = response.data.message || `Failed to initialize ${isDebtPayment ? 'debt ' : ''}payment`;
        setMobileMoneyError(errorMessage);
        console.error('API Error:', response.data);
      }

    } catch (error) {
      console.error('Mobile Money payment initialization error:', error);
      
      // Handle different types of errors
      if (error.code === 'ECONNABORTED') {
        setMobileMoneyError('Request timeout. Please check your internet connection and try again.');
      } else if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const errorData = error.response.data;
        
        console.error('Error Response:', {
          status,
          data: errorData
        });
        
        if (status === 500) {
          console.error('Server Error Details:', {
            message: errorData?.message,
            error: errorData?.error,
            stack: errorData?.stack
          });
          setMobileMoneyError('Server error occurred. Please contact support if this persists.');
        } else if (status === 403) {
          setMobileMoneyError('Access denied. IP address may not be whitelisted with payment gateway.');
        } else if (status === 400) {
          setMobileMoneyError(errorData?.message || 'Invalid request. Please check your input and try again.');
        } else {
          setMobileMoneyError(errorData?.message || `${isDebtPayment ? 'Debt p' : 'P'}ayment request failed (${status})`);
        }
      } else if (error.request) {
        setMobileMoneyError('No response from payment gateway. Please check your connection.');
      } else {
        setMobileMoneyError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setMobileMoneyLoading(false);
    }
  };

  const checkPaymentStatus = async (clientReference) => {
    try {
      // Validate client reference
      if (!clientReference || typeof clientReference !== 'string') {
        throw new Error('Invalid client reference provided');
      }

      console.log(`Checking payment status for client reference: ${clientReference}`);

      // Make request to backend endpoint
      const response = await axios.get(
        checkpaymentstatus(clientReference),
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          timeout: 15000, // Shorter timeout for status checks
          validateStatus: function (status) {
            return (status >= 200 && status < 300) || status === 404;
          }
        }
      );

      // Handle different response scenarios
      if (response.status === 404) {
        console.warn(`Payment with client reference ${clientReference} not found`);
        return {
          success: false,
          status: 'not_found',
          message: 'Payment record not found',
          clientReference: clientReference
        };
      }

      // Successful response
      const paymentData = response.data;
      console.log('Payment status response:', paymentData);

      // Check if the backend request was successful
      if (!paymentData.success) {
        return {
          success: false,
          status: 'error',
          message: paymentData.message || 'Failed to check payment status',
          clientReference: clientReference,
          error: paymentData.error
        };
      }

      // Extract status from backend response
      const statusData = paymentData.data;
      let normalizedStatus = 'pending';
      
      if (statusData.status === 'Paid' || statusData.status === 'paid' || statusData.status === 'success') {
        normalizedStatus = 'success';
      } else if (statusData.status === 'Failed' || statusData.status === 'failed' || statusData.status === 'error') {
        normalizedStatus = 'failed';
      } else if (statusData.status === 'Pending' || statusData.status === 'pending') {
        normalizedStatus = 'pending';
      }

      // Return standardized response
      return {
        success: true,
        status: normalizedStatus,
        message: `Payment status: ${statusData.status}`,
        clientReference: clientReference,
        data: {
          transactionId: statusData.transactionId,
          externalTransactionId: statusData.externalTransactionId,
          amount: statusData.amount,
          paymentMethod: statusData.paymentMethod,
          date: statusData.date,
          charges: statusData.charges,
          amountAfterCharges: statusData.amountAfterCharges
        }
      };

    } catch (error) {
      console.error('Payment status check error:', error);

      // Handle network/timeout errors
      if (error.code === 'ECONNABORTED') {
        return {
          success: false,
          status: 'timeout',
          message: 'Request timeout - please try again',
          clientReference: clientReference
        };
      }

      // Handle HTTP errors
      if (error.response) {
        const status = error.response.status;
        const statusText = error.response.statusText;
        
        console.error(`HTTP ${status}: ${statusText}`);
        
        if (status === 404) {
          return {
            success: false,
            status: 'not_found',
            message: 'Payment record not found',
            clientReference: clientReference
          };
        } else if (status === 403) {
          return {
            success: false,
            status: 'forbidden',
            message: 'Access forbidden - please contact support',
            clientReference: clientReference
          };
        } else {
          return {
            success: false,
            status: 'error',
            message: `HTTP ${status}: ${statusText}`,
            clientReference: clientReference,
            errorCode: status
          };
        }
      }

      // Handle other errors
      return {
        success: false,
        status: 'error',
        message: error.message || 'Unknown error occurred',
        clientReference: clientReference
      };
    }
  };

 
  const handleRegularPayment = async () => {
  const {
    selectedServiceCharges,
    selectedInvestigationCharges,
    selectedMedicationCharges,
    selectedOtherCharges
  } = getSelectedCharges();

  try {
    const loadingText = isDebtPayment ? 'Processing Debt Payment.......' : 'Processing.......';
    Swal.fire({
      title: loadingText,
      text: 'Please wait',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => { Swal.showLoading(); }
    });

    const patient_name = selectedPatient.fullName;
    const tel          = selectedPatient.tel;
    const res          = selectedPatient.res;
    const id           = selectedPatient.id;
    const gender       = selectedPatient.gender;
    const age          = selectedPatient.age;

    const paymentPayload = {
      paymentMethod,
      selectedPatient,
      currentUser,
      selectedServiceCharges,
      selectedInvestigationCharges,
      selectedMedicationCharges,
      selectedOtherCharges,
      selectedTotal,
      patient_name,
      tel,
      res,
      id,
      gender,
      age,
      isDebtPayment,
      paymentType,
      debtDetails: isDebtPayment ? { ...debtDetails } : null
    };

    const response = await axios.put(PaymentProcess, paymentPayload);

    if (response.data.success) {
      const successText = isDebtPayment
        ? 'Debt Payment Successfully Processed'
        : 'Payment Successfully Processed';
      successMessage('Save', successText);
      if (onPaymentSuccess) onPaymentSuccess();
      onClose();
    }

  } catch (error) {
    Swal.close(); // close loading spinner

    const responseData = error.response?.data;

    // ── ★ Handle out-of-stock expired medication ──────────────────────────
    if (responseData?.isStockError && responseData?.message?.startsWith('OUT_OF_STOCK::')) {
      const parts   = responseData.message.split('::');
      const medName = parts[1] || 'Unknown medication';
      const detail  = parts[2] || '';

      await Swal.fire({
        title: '⚠️ Medication Out of Stock',
        html: `
          <div style="text-align:left; padding: 8px 0;">
            <p style="margin-bottom:12px;">
              The following medication was <strong>previously reserved</strong> but the 
              reservation expired before payment was made. It has since run out of stock 
              at the dispensary.
            </p>
            <div style="
              background:#fff3e0; 
              border:1px solid #f59e0b; 
              border-left:4px solid #f59e0b;
              border-radius:8px; 
              padding:14px 16px;
              margin-bottom:12px;">
              <p style="font-weight:700; font-size:1rem; color:#92400e; margin:0 0 6px;">
                ${medName}
              </p>
              <p style="font-size:0.85rem; color:#78350f; margin:0;">
                ${detail}
              </p>
            </div>
            <p style="color:#555; font-size:0.9rem;">
              <strong>What to do:</strong><br/>
              • Ask the doctor to re-prescribe once the drug is restocked<br/>
              • The pharmacy can notify the store for an urgent restock<br/>
              • You can proceed with paying all other charges now
            </p>
          </div>
        `,
        icon: 'warning',
        confirmButtonText: 'Understood',
        confirmButtonColor: '#f59e0b',
        width: '500px'
      });

      return; // stop — do NOT close the payment dialog, let cashier deselect the item
    }

    // ── Generic error ─────────────────────────────────────────────────────
    await Swal.fire({
      title: 'Error',
      text: responseData?.message || 'Failed to Process Payment',
      icon: 'error'
    });
  }
};


  const handlePaymentSubmit = async () => {
  // Get readable payment method name
  const selectedMethodLabel = (() => {
    switch (paymentMethod) {
      case 'cash':
        return 'Cash Payment';
      case 'mobile_money':
        return 'Mobile Money';
      case 'mobile_money(manual)':
        return 'Mobile Money (Manual)';
      case 'credit':
        return 'Credit Bill';
      default:
        return paymentMethod;
    }
  })();

  // Show confirmation popup before proceeding
  const { isConfirmed } = await Swal.fire({
    title: 'Confirm Payment',
    html: `
      <div style="text-align:center;">
        <p>Are you sure you want to process this ${
          isDebtPayment ? 'debt ' : ''
        }payment?</p>
        <p style="font-weight:600; color:#2563eb; margin-top:8px;">
          Amount: ${formatCurrency(selectedTotal)}
        </p>
        <p style="margin:4px 0;">
          Patient: <strong>${selectedPatient.fullName || selectedPatient.name}</strong>
        </p>
        <p style="margin:4px 0; color:#374151;">
          Payment Method: <strong>${selectedMethodLabel}</strong>
        </p>
      </div>
    `,
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Yes, Process Payment',
    cancelButtonText: 'Cancel',
    reverseButtons: true,
    focusCancel: true,
  });

  if (isConfirmed) {
    // Proceed with payment
    if (paymentMethod === 'mobile_money') {
      await handleMobileMoneyPayment();
    } else {
      await handleRegularPayment();
    }
  } else {
    // User cancelled payment
    Swal.fire({
      title: 'Payment Cancelled',
      text: 'The payment process has been cancelled.',
      icon: 'info',
      timer: 3000,
      showConfirmButton: false,
    });
  }
};


  const handleClose = () => {
    // Clear any ongoing status checks
    if (statusCheckIntervalRef.current) {
      clearInterval(statusCheckIntervalRef.current);
      statusCheckIntervalRef.current = null;
    }
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Reset form state
    setPaymentMethod(defaultPaymentMethod);
    setMobileMoneyData({ payeeMobileNumber: '', channel: '' });
    setMobileMoneyError('');
    setMobileMoneyLoading(false);
    setMobileMoneyDigits(Array(10).fill(''));
    setIsCheckingStatus(false);
    setStatusCheckProgress(0);
    setPaymentStatusMessage('');
    onClose();
  };

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (statusCheckIntervalRef.current) {
        clearInterval(statusCheckIntervalRef.current);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Validate if current payment method is allowed
  const isCurrentMethodAllowed = allowedPaymentMethods.includes(paymentMethod);

  // Auto-select first allowed method if current is not allowed
  React.useEffect(() => {
    if (!isCurrentMethodAllowed && paymentMethods.length > 0) {
      setPaymentMethod(paymentMethods[0].id);
    }
  }, [allowedPaymentMethods, paymentMethods, isCurrentMethodAllowed]);

  if (!selectedPatient) return null;

  // Determine dialog title and icon based on payment type
  const dialogTitle = isDebtPayment ? 'Process Debt Payment' : 'Process Payment';
  const dialogIcon = isDebtPayment ? DebtIcon : CreditCardIcon;
  const dialogSubtitle = isDebtPayment ? 'Secure debt payment processing' : 'Secure payment processing';

  return (
    <>
      <Dialog 
        open={open} 
        maxWidth="sm" 
        fullWidth
        disableEscapeKeyDown={isCheckingStatus}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          pb: 1,
          background: 'linear-gradient(135deg, #2c8a2cff 0%, #386626ff 100%)',
          color: 'white',
          borderRadius: '12px 12px 0 0'
        }}>
          <Box display="flex" flexDirection="column" alignItems="center">
            <Box 
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)', 
                borderRadius: '50%', 
                p: 1.5, 
                mb: 1 
              }}
            >
              {React.createElement(dialogIcon, { sx: { fontSize: 32, color: 'white' } })}
            </Box>
            <Typography variant="h5" fontWeight="600">
              {dialogTitle}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
              {dialogSubtitle}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: 4, py: 3 }}>
          {/* Debt Payment Information Banner */}
          {isDebtPayment && debtDetails && (
            <Box 
              sx={{ 
                mb: 3, 
                p: 3, 
                bgcolor: '#fef3c7', 
                borderRadius: 2, 
                border: '1px solid #b41409ff',
                borderLeft: '4px solid #b41409ff'
              }}
            >
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <DebtIcon sx={{ color: '#b41409ff', fontSize: 24 }} />
                <Typography variant="h6" sx={{ color: '#92400e', fontWeight: 600 }}>
                  Debt Payment Details
                </Typography>
              </Box>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                {debtDetails.debtId && (
                  <Box>
                    <Typography variant="caption" color="#78350f" fontWeight={600}>
                      Debt ID
                    </Typography>
                    <Typography variant="body2" color="#92400e">
                      {debtDetails.debtId}
                    </Typography>
                  </Box>
                )}
                
                {debtDetails.originalAmount && (
                  <Box>
                    <Typography variant="caption" color="#78350f" fontWeight={600}>
                      Original Amount
                    </Typography>
                    <Typography variant="body2" color="#92400e">
                      {formatCurrency(debtDetails.originalAmount)}
                    </Typography>
                  </Box>
                )}
                
                {debtDetails.remainingAmount && (
                  <Box>
                    <Typography variant="caption" color="#78350f" fontWeight={600}>
                      Remaining Amount
                    </Typography>
                    <Typography variant="body2" color="#92400e">
                      {formatCurrency(debtDetails.remainingAmount)}
                    </Typography>
                  </Box>
                )}
                
                {debtDetails.debtDate && (
                  <Box>
                    <Typography variant="caption" color="#78350f" fontWeight={600}>
                      Debt Date
                    </Typography>
                    <Typography variant="body2" color="#92400e">
                      {new Date(debtDetails.debtDate).toLocaleDateString()}
                    </Typography>
                  </Box>
                )}
              </Box>
              
              {debtDetails.debtDescription && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="#78350f" fontWeight={600}>
                    Description
                  </Typography>
                  <Typography variant="body2" color="#92400e">
                    {debtDetails.debtDescription}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Status Checking Progress */}
          {isCheckingStatus && (
            <Box sx={{ mb: 3, p: 3, bgcolor: '#f8f9ff', borderRadius: 2, border: '1px solid #e3f2fd' }}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <AccessTimeIcon sx={{ color: '#1976d2' }} />
                <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 600 }}>
                  Verifying Payment
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {paymentStatusMessage}
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={statusCheckProgress} 
                sx={{ 
                  height: 8, 
                  borderRadius: 4,
                  bgcolor: '#e3f2fd',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: '#1976d2'
                  }
                }} 
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                This may take up to 2 minutes. Please don't close this window.
              </Typography>
            </Box>
          )}

          {/* Payment Amount Section */}
          <Box 
            sx={{ 
              mb: 4,
              p: 3,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
              textAlign: 'center',
              border: isDebtPayment ? '1px solid #f59e0b' : '1px solid #e0e7ff'
            }}
          >
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {isDebtPayment ? 'Debt Payment Amount' : 'Payment Amount'}
            </Typography>
            <Typography 
              variant="h3" 
              fontWeight="bold" 
              sx={{ 
                color:  '#1976d2',
                textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                mb: 1
              }}
            >
              {formatCurrency(selectedTotal)}
            </Typography>
            <Chip 
              label={isDebtPayment ? 'Debt Amount Due' : 'Total Due'} 
              size="small" 
              sx={{ 
                bgcolor: '#1976d2', 
                color: 'white',
                fontWeight: 600
              }} 
            />
            
            {/* Payment Type Indicator */}
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 1 }}>
              <Chip 
                icon={isDebtPayment ? <DebtIcon sx={{ fontSize: 16 }} /> : <ReceiptIcon sx={{ fontSize: 16 }} />}
                label={paymentType === 'partial_debt' ? 'Partial Debt Payment' : 
                      isDebtPayment ? 'Debt Payment' : 'Regular Payment'}
                variant="outlined"
                size="small"
                sx={{ 
                  borderColor:  '#1976d2',
                  color: '#1976d2'
                }}
              />
            </Box>
          </Box>

          {/* Conditional Payment Method Section - Only show if showPaymentMethods is true and not checking status */}
          {showPaymentMethods && !isCheckingStatus && paymentMethods.length > 0 && (
            <FormControl component="fieldset" sx={{ width: '100%', mb: 3 }}>
              <Typography 
                variant="h6" 
                gutterBottom 
                sx={{ mb: 2, fontWeight: 600, color: '#1a202c' }}
              >
                Choose Payment Method
              </Typography>

              {paymentMethods.length === 1 ? (
                // If only one payment method is allowed, show it as selected but not changeable
                <Box sx={{ mb: 2 }}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Only {paymentMethods[0].label} is available for this {isDebtPayment ? 'debt payment' : 'transaction'}.
                  </Alert>
                </Box>
              ) : null}

              <RadioGroup
                value={paymentMethod}
                onChange={(e) => {
                  setPaymentMethod(e.target.value);
                  setMobileMoneyError('');
                  if (e.target.value !== 'mobile_money') {
                    setMobileMoneyData({ payeeMobileNumber: '', channel: '' });
                  }
                }}
                sx={{ gap: 1 }}
              >
                {paymentMethods.map((method) => {
                  const IconComponent = method.icon;
                  const isSelected = paymentMethod === method.id;

                  return (
                    <Box
                      key={method.id}
                      sx={{
                        border: isSelected ? '2px solid #1976d2' : '2px solid #e2e8f0',
                        borderRadius: 2,
                        transition: 'all 0.3s ease',
                        background: isSelected
                          ? 'linear-gradient(135deg, #ebf4ff 0%, #dbeafe 100%)'
                          : 'white',
                        '&:hover': {
                          borderColor: isSelected ? '#1976d2' : '#cbd5e0',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        },
                        p: 2,
                      }}
                    >
                      <FormControlLabel
                        value={method.id}
                        control={
                          <Radio
                            sx={{
                              color: isSelected ? '#1976d2' : '#64748b',
                              '&.Mui-checked': { color: '#1976d2' },
                            }}
                            disabled={paymentMethods.length === 1} // Disable radio if only one option
                          />
                        }
                        label={
                          <Box display="flex" alignItems="center" gap={2} sx={{ width: '100%' }}>
                            <Box
                              sx={{
                                p: 1,
                                borderRadius: 1,
                                bgcolor: isSelected ? '#1976d2' : '#f1f5f9',
                                color: isSelected ? 'white' : '#64748b',
                                transition: 'all 0.3s ease',
                              }}
                            >
                              <IconComponent sx={{ fontSize: 24 }} />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography
                                variant="body1"
                                fontWeight={isSelected ? 600 : 500}
                                sx={{ color: isSelected ? '#1976d2' : '#1a202c' }}
                              >
                                {method.label}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {method.description}
                              </Typography>
                            </Box>
                            {isSelected && (
                              <CheckCircleIcon sx={{ color: '#10b981', fontSize: 20 }} />
                            )}
                          </Box>
                        }
                        sx={{ margin: 0, width: '100%' }}
                      />

                      {/* Mobile Money Details - Only show if mobile money is selected and allowed */}
                      {method.id === 'mobile_money' && isSelected && allowedPaymentMethods.includes('mobile_money') && (
                        <Box sx={{ mt: 2 }}>
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              justifyContent: 'center', 
                              gap: 0.4, 
                              mb: 2,
                              flexWrap: 'nowrap'
                            }}
                          >
                            {Array.from({ length: 10 }).map((_, index) => (
                              <TextField
                                key={index}
                                value={mobileMoneyDigits[index] || ''}
                                onChange={(e) => {
                                  const val = e.target.value.replace(/\D/g, '');
                                  if (!val) return;
                                  const newDigits = [...mobileMoneyDigits];
                                  newDigits[index] = val[val.length - 1];
                                  setMobileMoneyDigits(newDigits);
                                  if (index < 9) {
                                    document.getElementById(`digit-${index + 1}`)?.focus();
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Backspace' && !mobileMoneyDigits[index] && index > 0) {
                                    document.getElementById(`digit-${index - 1}`)?.focus();
                                  }
                                }}
                                inputProps={{
                                  maxLength: 1,
                                  style: { 
                                    textAlign: 'center',
                                    fontSize: '1rem',
                                    width: '1.3rem',
                                    height: '1.6rem'
                                  }
                                }}
                                id={`digit-${index}`}
                              />
                            ))}
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            Enter your 10-digit mobile money number
                          </Typography>

                          <Box sx={{ mt: 1, textAlign: 'center' }}>
                            <Button 
                              size="small"
                              variant="outlined"
                              color="secondary"
                              onClick={() => setMobileMoneyDigits(Array(10).fill(''))}
                            >
                              Clear Number
                            </Button>
                          </Box>

                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, mt: 2 }}>
                            Select your mobile money provider:
                          </Typography>

                          <RadioGroup
                            row
                            value={mobileMoneyData.channel}
                            onChange={handleMobileMoneyChannelChange}
                            sx={{ gap: 2, justifyContent: 'center' }}
                          >
                            {mobileMoneyChannels.map((channel) => {
                              const isChannelSelected = mobileMoneyData.channel === channel.id;
                              return (
                                <FormControlLabel
                                  key={channel.id}
                                  value={channel.id}
                                  control={
                                    <Radio 
                                      sx={{ 
                                        color: isChannelSelected ? '#1976d2' : '#64748b',
                                        '&.Mui-checked': { color: '#1976d2' }
                                      }} 
                                    />
                                  }
                                  label={channel.label}
                                  sx={{
                                    border: isChannelSelected
                                      ? '2px solid #1976d2'
                                      : '1px solid #e2e8f0',
                                    borderRadius: 2,
                                    px: 2,
                                    py: 1,
                                    bgcolor: isChannelSelected ? '#ebf4ff' : 'white',
                                    flex: '1 1 auto',
                                    textAlign: 'center'
                                  }}
                                />
                              );
                            })}
                          </RadioGroup>

                          {mobileMoneyError && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                              {mobileMoneyError}
                            </Alert>
                          )}

                          <Box sx={{ mt: 2, p: 2, bgcolor: '#fff7ed', borderRadius: 2 }}>
                            <Typography variant="body2" fontWeight={600} color="#ea580c">
                              Mobile Money {isDebtPayment ? 'Debt ' : ''}Payment Process:
                            </Typography>
                            <Typography variant="caption" color="#9a3412" component="div">
                              1. Enter your number<br />
                              2. Select your provider<br />
                              3. Click "Process {isDebtPayment ? 'Debt ' : ''}Payment"<br />
                              4. Approve prompt on your phone<br />
                              5. Enter PIN to complete
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </RadioGroup>
            </FormControl>
          )}

          {/* Message when no payment methods are available */}
          {showPaymentMethods && paymentMethods.length === 0 && !isCheckingStatus && (
            <Box sx={{ mb: 3 }}>
              <Alert severity="warning">
                No payment methods are currently available for this {isDebtPayment ? 'debt payment' : 'transaction'}. Please contact support for assistance.
              </Alert>
            </Box>
          )}

          {/* Message when payment methods are hidden */}
          {!showPaymentMethods && !isCheckingStatus && (
            <Box sx={{ mb: 3, p: 3, bgcolor: '#f8f9fa', borderRadius: 2, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                Payment method has been pre-selected for this {isDebtPayment ? 'debt payment' : 'transaction'}.
              </Typography>
              <Chip 
                label={allPaymentMethods.find(m => m.id === paymentMethod)?.label || paymentMethod}
                color="primary"
                sx={{ mt: 1 }}
              />
            </Box>
          )}

          {/* Security Notice - Conditional display */}
          {showSecurityNotice && (
            <Box 
              sx={{ 
                mt: 3, 
                p: 2, 
                bgcolor: '#f8fafc', 
                borderRadius: 2,
                border: '1px solid #e2e8f0'
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <SecurityIcon sx={{ fontSize: 18, color: '#10b981' }} />
                <Typography variant="body2" fontWeight={600} color="#059669">
                  Secure Transaction
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                Your {isDebtPayment ? 'debt ' : ''}payment is protected by industry-standard encryption and security measures.
                {paymentMethod === 'mobile_money' && ` Mobile money ${isDebtPayment ? 'debt ' : ''}payments are processed directly through your mobile network operator's secure platform.`}
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions 
          sx={{ 
            px: 4, 
            pb: 3,
            pt: 2,
            gap: 2,
            justifyContent: 'center'
          }}
        >
          {!isCheckingStatus && (
            <>
              <Button 
                onClick={handleClose}
                variant="outlined"
                size="large"
                sx={{ 
                  minWidth: 120,
                  borderColor: '#cbd5e0',
                  color: '#64748b',
                  '&:hover': {
                    borderColor: '#94a3b8',
                    backgroundColor: '#f8fafc'
                  }
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={handlePaymentSubmit} 
                variant="contained"
                size="large"
                disabled={
                  !paymentMethod || 
                  mobileMoneyLoading || 
                  (paymentMethods.length === 0) ||
                  (paymentMethod === 'mobile_money' && enableMobileMoneyValidation && (mobileMoneyDigits.join('').length !== 10 || !mobileMoneyData.channel))
                }
                startIcon={mobileMoneyLoading ? 
                  <CircularProgress size={20} color="inherit" /> : 
                  <PaymentIcon sx={{ fontSize: 20 }} />
                }
                sx={{ 
                  minWidth: 140,
                  background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                  '&:hover': {
                    background:'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                  },
                  '&:disabled': {
                    background: '#cbd5e0',
                    color: '#64748b'
                  }
                }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  {mobileMoneyLoading ? 
                    `Processing ${isDebtPayment ? 'Debt Payment' : 'Payment'}...` : 
                    paymentMethod === 'mobile_money' ? 
                      `Send Mobile Money ${isDebtPayment ? 'Debt Payment ' : ''}Request` : 
                      `Process ${isDebtPayment ? 'Debt ' : ''}Payment`
                  }
                </Box>
              </Button>
            </>
          )}
          
          {isCheckingStatus && (
            <Box sx={{ textAlign: 'center', width: '100%' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Please wait while we verify your {isDebtPayment ? 'debt ' : ''}payment...
              </Typography>
              <Button 
                onClick={() => {
                  Swal.fire({
                    title: `Stop ${isDebtPayment ? 'Debt ' : ''}Payment Verification?`,
                    text: `Are you sure you want to stop the ${isDebtPayment ? 'debt ' : ''}payment verification process?`,
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Yes, Stop',
                    cancelButtonText: 'Continue Waiting'
                  }).then((result) => {
                    if (result.isConfirmed) {
                      handleClose();
                    }
                  });
                }}
                variant="outlined"
                color="secondary"
                size="small"
              >
                Cancel Verification
              </Button>
            </Box>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PaymentDialog;